import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next"; // <-- 1. Import useTranslation
import { useGetCategoriesQuery } from "../../../api/productTaxonomyApi";
import { useLazyGetProductsQuery } from "../../../api/productApi";
import { useProductsUrlSync } from "../../../hooks";
import { setCategory, setFilters, setPagination } from "../../../productSlice";
import CollectionsSlider from "./CollectionsSlider";
import ProductsSlider from "./ProductsSlider";
import RTKQErrorCard from "../../../../../../common/components/RTKQErrorCard";
import type { AppDispatch, RootState } from "../../../../../../app/store";
import type { FilterState, PaginationState } from "../../../productSlice";
import type { TransformedWPCategory } from "../../../types/productTaxonomyApiTypes";

const CollectionsContainer: React.FC = () => {
  const { t } = useTranslation("common"); // <-- 2. Initialize translation hook
  const dispatch = useDispatch<AppDispatch>();

  // Get filter state from Redux
  const filters = useSelector((state: RootState) => state.product.filters);
  const pagination = useSelector(
    (state: RootState) => state.product.pagination,
  );

  // Memoized setters for URL sync hook
  const memoizedSetFilters = useCallback(
    (filtersData: Partial<FilterState>) => {
      dispatch(setFilters(filtersData));
    },
    [dispatch],
  );
  const memoizedSetPagination = useCallback(
    (paginationData: Partial<PaginationState>) => {
      dispatch(setPagination(paginationData));
    },
    [dispatch],
  );

  // Initialize URL sync
  const urlSyncComplete = useProductsUrlSync(
    filters,
    pagination,
    memoizedSetFilters,
    memoizedSetPagination,
  );

  // Fetch categories
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    error: categoriesError,
  } = useGetCategoriesQuery();

  // Lazy query for products
  const [
    triggerGetProducts,
    {
      data: productsData,
      isLoading: isProductsLoading,
      isFetching: isProductsFetching,
      isError: isProductsError,
      error: productsError,
    },
  ] = useLazyGetProductsQuery();

  const topLevelCategories: TransformedWPCategory[] = useMemo(() => {
    // FIX: Use the 'items' array which preserves the original order from the server
    if (!categoriesData?.items) return [];

    return categoriesData.items.filter(
      (cat) => cat.parent === 0 && cat.count > 0,
    );
  }, [categoriesData]);

  const activeCategoryId = useMemo(() => {
    return filters.category[0] || null;
  }, [filters.category]);

  // Effect to set the first category as active if none is selected and categories have loaded
  useEffect(() => {
    if (urlSyncComplete && !activeCategoryId && topLevelCategories.length > 0) {
      dispatch(setCategory({ replace: [topLevelCategories[0].id] }));
    }
  }, [urlSyncComplete, activeCategoryId, topLevelCategories, dispatch]);

  // Effect to fetch products when active category changes
  useEffect(() => {
    if (activeCategoryId) {
      triggerGetProducts({ category: [activeCategoryId], limit: 20 });
    }
  }, [activeCategoryId, triggerGetProducts]);

  const handleCategorySelect = (categoryId: number) => {
    if (categoryId !== activeCategoryId) {
      dispatch(setCategory({ replace: [categoryId] }));
    }
  };

  const products = productsData?.products || [];

  return (
    <div className="collections-page">
      <section className="collections-section">
        {/* <-- 3. Use the translation key for Collections --> */}
        <h2 className="collections-section__title">
          {t("productArchive.collectionsTitle")}
        </h2>
        {isCategoriesError && <RTKQErrorCard error={categoriesError as any} />}
        <CollectionsSlider
          categories={topLevelCategories}
          activeCategoryId={activeCategoryId}
          onCategoryClick={handleCategorySelect}
          isLoading={isCategoriesLoading}
        />
      </section>

      {/*       <section className="products-section">
        <h2 className="products-section__title">{t("productArchive.productsTitle")}</h2>
        {isProductsError && <RTKQErrorCard error={productsError as any} />}
        <ProductsSlider
          products={products}
          isLoading={isProductsLoading || isProductsFetching}
          activeCategoryId={activeCategoryId}
        />
      </section> */}
    </div>
  );
};

export default CollectionsContainer;
